import React from "react";
import { ClerkProvider } from "./clerkReact";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertIcon, Box, Code, Heading, Stack, Text } from "@chakra-ui/react";
import { getConfigValue } from "../../config/runtimeConfig";

const ClerkAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const publishableKey = getConfigValue(
    "REACT_APP_CLERK_PUBLISHABLE_KEY",
    process.env.REACT_APP_CLERK_PUBLISHABLE_KEY
  );
  const isDevelopment = process.env.NODE_ENV === "development";

  const signInRedirectUrl =
    getConfigValue("REACT_APP_CLERK_AFTER_SIGN_IN_URL", process.env.REACT_APP_CLERK_AFTER_SIGN_IN_URL, "/dashboard");
  const signUpRedirectUrl =
    getConfigValue("REACT_APP_CLERK_AFTER_SIGN_UP_URL", process.env.REACT_APP_CLERK_AFTER_SIGN_UP_URL, "/dashboard");

  const handleNavigate = (to, replace = false) => {
    if (typeof to === "string" && /^https?:\/\//i.test(to)) {
      return replace ? window.location.replace(to) : window.location.assign(to);
    }
    navigate(to, replace ? { replace: true } : undefined);
  };

  if (!publishableKey) {
    return (
      <Box minH="100vh" bg="gray.50" px={6} py={10}>
        <Box maxW="840px" mx="auto" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={6}>
          <Stack spacing={4}>
            <Heading size="md">Clerk Configuration Required</Heading>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                {isDevelopment ? (
                  <>
                    Missing <Code>REACT_APP_CLERK_PUBLISHABLE_KEY</Code>. Clerk cannot initialize.
                  </>
                ) : (
                  "Authentication service is not configured."
                )}
              </AlertDescription>
            </Alert>
            {isDevelopment ? (
              <>
                <Text color="gray.700">
                  Add your Clerk publishable key to <Code>.env.development</Code> and restart the dev server.
                </Text>
                <Code p={3} whiteSpace="pre-wrap">
                  REACT_APP_AUTH_PROVIDER=CLERK{"\n"}
                  REACT_APP_CLERK_PUBLISHABLE_KEY=pk_test_xxxxx
                </Code>
              </>
            ) : (
              <Text color="gray.700">
                Contact the platform administrator to complete authentication configuration.
              </Text>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInUrl={getConfigValue("REACT_APP_CLERK_SIGN_IN_URL", process.env.REACT_APP_CLERK_SIGN_IN_URL, "/login")}
      signUpUrl={getConfigValue("REACT_APP_CLERK_SIGN_UP_URL", process.env.REACT_APP_CLERK_SIGN_UP_URL, "/sign-up")}
      signInForceRedirectUrl={signInRedirectUrl}
      signInFallbackRedirectUrl={signInRedirectUrl}
      signUpForceRedirectUrl={signUpRedirectUrl}
      signUpFallbackRedirectUrl={signUpRedirectUrl}
      routerPush={(to) => handleNavigate(to)}
      routerReplace={(to) => handleNavigate(to, true)}
    >
      {children}
    </ClerkProvider>
  );
};

export default ClerkAuthProvider;
